import React, { useState, useEffect } from 'react';
import { UserInfoBarProps } from '../../types/game.types';
import { useGameManager } from '../../hooks/useGameManager';
import { useAppContext } from '../../hooks/useAppContext';
import {
  FaCrown,
  FaDoorOpen,
  FaUsers,
  FaChevronDown,
  FaCircle,
  FaSpinner,
  FaQuestionCircle,
  FaHome,
  FaCog,
  FaComments,
  FaLock,
  FaUnlock,
  FaPalette,
  FaShieldAlt
} from 'react-icons/fa';
import { PlatformRole } from 'minka-shared';
import { Button, IconButton, Dropdown, DropdownItem, Modal } from '../common';
import { getStatusTextColor, getStatusLabel } from '../../utils/statusColors';
import PlayerAvatar from '../common/PlayerAvatar';
import { MediaToggleControls } from '../video/MediaToggleControls.js';

const UserInfoBar: React.FC<UserInfoBarProps> = ({ className, roomConnection, onChatToggle, onCustomizeIslandToggle, customizeIslandDisabledHint, totalUnreadCount }) => {
  const { auth, room: roomContext, platformAdmin } = useAppContext();
  const { openAdminPanel, openUserSettingsModal } = useGameManager();

  // Local state for status management (replacing useUserStatus)
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  // The game manager never carried a user or a room — the authenticated user
  // and the island the connection reports are the only sources.
  const user = auth.user;
  const room = roomConnection.currentRoom || roomContext.currentRoom;

  // Get current user's status from playersData
  const userStatus = roomConnection.playersData.find(p => p.userId === user?.id)?.status || 'available';

  // Get current user's role from playersData (same pattern as status)
  const userRole = roomConnection.playersData.find(p => p.userId === user?.id)?.role;

  // Get current private area if player is in one
  const getCurrentPrivateArea = () => {
    if (!user?.id || !roomConnection.privateAreas) {
      return null;
    }

    // Check if player is in any private area
    for (const area of roomConnection.privateAreas) {
      const isInArea = area.occupants?.some(occupant => occupant.userId === user.id);
      if (isInArea) {
        return area;
      }
    }

    return null;
  };

  const [currentPrivateArea, setCurrentPrivateArea] = useState(() => getCurrentPrivateArea());
  const [isTogglingDoor, setIsTogglingDoor] = useState(false);

  // Re-check private area when privateAreas changes
  useEffect(() => {
    const area = getCurrentPrivateArea();
    setCurrentPrivateArea(area);
  }, [roomConnection.privateAreas, user?.id]);

  // Helper function to get display name for roles
  const getRoleDisplayName = (role: string): string => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'moderator':
        return 'Moderator';
      case 'guest':
        return 'Guest';
      case 'member':
      case 'player':
      default:
        return 'Resident';
    }
  };

  // Dropdown open state (controlled so we can close on item click)
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  // Opening the console leaves the island, so it is asked for first (#65).
  const [isConfirmingConsole, setIsConfirmingConsole] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [showPlayersDropdown, setShowPlayersDropdown] = useState(false);

  // Clear error message when status dropdown is opened
  useEffect(() => {
    if (showStatusDropdown && statusError) {
      setStatusError(null);
    }
  }, [showStatusDropdown, statusError]);

  const handleExitRoom = async (): Promise<void> => {
    try {
      // Use React WebSocket connection to leave room
      await roomConnection.leaveRoom();
      // Also clear context room state
      roomContext.clearRoomState();
    } catch (error) {
      console.error('Error exiting room:', error);
      // Still clear room state to ensure user can navigate
      roomContext.clearRoomState();
    }
  };

  // Register callback bridges for room exit and status updates only
  useEffect(() => {
    (window as any).roomExitBridge = { triggerExit: () => void handleExitRoom() };
    (window as any).statusBridge = {
      updateLocalStatus: (_status: string) => {
        // This is called when status changes locally - already handled by useUserStatus
      },
      updatePlayerStatus: (_userId: string, _status: string) => {
        // This is called when other players change status - name tags handled by Phaser
      }
    };

    return () => {
      (window as any).roomExitBridge = null;
      (window as any).statusBridge = null;
    };
  }, [handleExitRoom]);

  const isAdmin = userRole === 'admin';
  // The platform axis, not the island one: an island admin is not a superadmin
  // and a superadmin standing on an island may well be an ordinary resident.
  const isSuperadmin = user?.platformRole === PlatformRole.SUPERADMIN;

  const handleAdminPanel = () => {
    setShowMenuDropdown(false);
    openAdminPanel();
  };

  /**
   * The console cannot be opened from inside an island: it is the platform
   * view, and a superadmin operating it is not visiting anything. So opening it
   * ends the visit — which is worth asking about first, because nothing else in
   * this menu takes the operator off the island they are standing on.
   */
  const handlePlatformAdmin = () => {
    setShowMenuDropdown(false);
    setIsConfirmingConsole(true);
  };

  /**
   * Leave, then open. The exit is handed to the console rather than awaited
   * here: it opens at once, and waits for the island to have been let go before
   * it asks the server who is online — presence is per-island, so an earlier
   * answer would show the operator still standing where they just left (§6.7).
   */
  const confirmPlatformAdmin = () => {
    setIsConfirmingConsole(false);
    platformAdmin.open(handleExitRoom);
  };

  const handleCustomizeIsland = () => {
    setShowMenuDropdown(false);
    onCustomizeIslandToggle?.();
  };

  const handleHelp = () => {
    setShowMenuDropdown(false);
    // Placeholder for help functionality
  };

  const handleSettings = () => {
    setShowMenuDropdown(false);
    openUserSettingsModal();
  };

  const handlePlayerClick = (player: any) => {
    setShowPlayersDropdown(false);

    // Convert PlayerData to format expected by modal
    const modalData = {
      userId: player.userId,
      username: player.username,
      role: player.role || 'member',
      isLocal: player.userId === user?.id
    };

    // Use same bridge function as Phaser for consistent behavior
    (window as any).showPlayerDetails(modalData);
  };

  const handleDoorToggle = async () => {
    if (!currentPrivateArea || isTogglingDoor) return;

    setIsTogglingDoor(true);
    try {
      await roomConnection.togglePrivateAreaDoor(currentPrivateArea.areaId);
    } catch (error) {
      console.error('Failed to toggle door:', error);
    } finally {
      setIsTogglingDoor(false);
    }
  };

  const handleStatusChange = async (status: 'available' | 'busy' | 'dnd') => {
    setShowStatusDropdown(false);

    // Clear any previous error
    setStatusError(null);
    setPendingStatus(status);
    setIsChangingStatus(true);

    try {
      // Use Promise-based status change from roomConnection
      await roomConnection.changeUserStatus(status);
      // Success - status will be updated via WebSocket event
      setPendingStatus(null);
    } catch (error) {
      // Handle error
      const errorMessage = error instanceof Error ? error.message : 'Failed to update status';
      setStatusError(errorMessage);
      setPendingStatus(null);
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Status colors and labels now centralized in utils/statusColors.ts

  // Don't render if user is not authenticated
  if (!user) {
    return null;
  }

  return (
    <>
    <div className={`fixed bottom-0 left-0 right-0 h-16 bg-game-dark border-t border-gray-700 shadow-lg z-[2001] pointer-events-auto ${className || ''}`}>
      <div className="flex items-center justify-between h-full px-3 md:px-6 text-sm md:text-base">

        {/* Left Section - User Controls */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Menu Button */}
          <Dropdown
            trigger={({ toggle }) => (
              <IconButton
                icon={
                  <img
                    src="/assets/logo/minka-icon-white.png"
                    alt="Menu"
                    className="w-6 h-6 object-contain"
                  />
                }
                onClick={toggle}
                ariaLabel="Menu"
                aria-expanded={showMenuDropdown}
                aria-haspopup="true"
              />
            )}
            isOpen={showMenuDropdown}
            onOpenChange={setShowMenuDropdown}
            minWidth={customizeIslandDisabledHint ? '260px' : '180px'}
          >
            {isAdmin && (
              <>
                <DropdownItem
                  icon={<FaCrown className="text-game-golden group-hover:text-game-accent" />}
                  label="Admin Panel"
                  onClick={handleAdminPanel}
                />
                <DropdownItem
                  icon={<FaPalette className="text-game-teal group-hover:text-game-golden" />}
                  label="Customize Island"
                  sublabel={customizeIslandDisabledHint}
                  disabled={Boolean(customizeIslandDisabledHint)}
                  onClick={handleCustomizeIsland}
                />
              </>
            )}
            {isSuperadmin && (
              <DropdownItem
                icon={<FaShieldAlt className="text-game-golden group-hover:text-game-accent" />}
                label="Platform admin"
                onClick={handlePlatformAdmin}
              />
            )}
            <DropdownItem
              icon={<FaCog className="text-game-accent group-hover:text-game-golden" />}
              label="Settings"
              onClick={handleSettings}
            />
            <DropdownItem
              icon={<FaQuestionCircle className="text-game-light group-hover:text-game-teal" />}
              label="Help"
              onClick={handleHelp}
            />
          </Dropdown>

          {/* User Info */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* User Avatar */}
            <PlayerAvatar username={user.username} avatar={user.avatar} size="md" />

            {/* Username and Role */}
            <div className="hidden sm:flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
              <span className="text-gray-100 font-semibold text-sm md:text-base">{user.username}</span>
              {userRole && typeof userRole === 'string' && userRole === 'admin' && (
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-game-orange/20 to-game-red/20 border border-game-orange/30 text-game-accent px-2 py-0.5 text-xs font-semibold uppercase rounded-full">
                  <FaCrown className="text-game-golden text-xs" />
                  {getRoleDisplayName(userRole)}
                </div>
              )}
            </div>

            {/* Media Controls - Mic and Camera, driven by the live publish state */}
            <MediaToggleControls />
          </div>

          {/* Status */}
          <Dropdown
            trigger={({ isOpen, toggle }) => (
              <Button
                variant="secondary"
                onClick={toggle}
                className="px-3 h-10 min-w-[140px] md:min-w-[170px] text-white"
              >
                <FaCircle
                  className={`text-xs flex-shrink-0 ${getStatusTextColor(userStatus)}`}
                />
                <span className="font-medium text-sm">{getStatusLabel(userStatus)}</span>
                <FaChevronDown className={`text-gray-400 text-xs transition-transform duration-200 flex-shrink-0 ml-auto ${isOpen ? 'rotate-180' : ''}`} />
              </Button>
            )}
            isOpen={showStatusDropdown}
            onOpenChange={setShowStatusDropdown}
            minWidth="200px"
          >
            <DropdownItem
              icon={<FaCircle className={`${getStatusTextColor('available')} text-xs group-hover:scale-110 transition-transform`} />}
              label="Available"
              onClick={() => !isChangingStatus && handleStatusChange('available')}
              disabled={isChangingStatus}
              trailing={pendingStatus === 'available' ? <FaSpinner className="text-gray-400 animate-spin text-xs" /> : undefined}
            />
            <DropdownItem
              icon={<FaCircle className="text-game-golden text-xs group-hover:scale-110 transition-transform" />}
              label="Busy"
              onClick={() => !isChangingStatus && handleStatusChange('busy')}
              disabled={isChangingStatus}
              trailing={pendingStatus === 'busy' ? <FaSpinner className="text-gray-400 animate-spin text-xs" /> : undefined}
            />
            <DropdownItem
              icon={<FaCircle className="text-game-orange text-xs group-hover:scale-110 transition-transform" />}
              label="Do Not Disturb"
              onClick={() => !isChangingStatus && handleStatusChange('dnd')}
              disabled={isChangingStatus}
              trailing={pendingStatus === 'dnd' ? <FaSpinner className="text-gray-400 animate-spin text-xs" /> : undefined}
            />
          </Dropdown>
        </div>

        {/* Right Section - Room and Actions */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Chat Button */}
          <div className="relative group">
            <IconButton
              icon={<FaComments className="text-base" />}
              onClick={onChatToggle}
              ariaLabel="Toggle chat (Ctrl+K)"
              className="h-10 px-3 relative"
            />
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Toggle Chat (Ctrl+K)
            </div>
            {(totalUnreadCount ?? 0) > 0 && (
              <span className="absolute -top-1 -right-1 bg-game-accent text-white text-xs font-bold
                             min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
                {(totalUnreadCount ?? 0) > 99 ? '99+' : totalUnreadCount}
              </span>
            )}
          </div>

          {/* Private Area Door Toggle Button */}
          {currentPrivateArea && (
            <div className="relative group">
              <Button
                variant="secondary"
                onClick={handleDoorToggle}
                disabled={isTogglingDoor}
                className="px-3 h-10 text-white flex items-center gap-2"
                title={currentPrivateArea.isDoorOpen ? 'Lock private area' : 'Unlock private area'}
              >
                {isTogglingDoor ? (
                  <FaSpinner className="animate-spin text-sm" />
                ) : currentPrivateArea.isDoorOpen ? (
                  <FaUnlock className="text-game-teal text-sm" />
                ) : (
                  <FaLock className="text-game-orange text-sm" />
                )}
                <span className="hidden md:inline text-sm">
                  {currentPrivateArea.isDoorOpen ? 'Lock' : 'Unlock'}
                </span>
              </Button>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {currentPrivateArea.name} - {currentPrivateArea.isDoorOpen ? 'Open' : 'Closed'}
              </div>
            </div>
          )}

          {/* Combined Room Button */}
          <Dropdown
            trigger={({ isOpen, toggle }) => (
              room ? (
                <Button
                  variant="secondary"
                  onClick={toggle}
                  type="button"
                  title="View island players"
                  className="px-3 h-10 text-white min-w-[140px] md:min-w-[180px]"
                >
                  <FaHome className="text-game-golden text-sm flex-shrink-0" />
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span className="text-gray-100 font-semibold text-sm truncate w-full">
                      {room.name}
                    </span>
                    {!roomConnection.isConnected && room && (
                      <span className="text-game-orange font-medium text-xs animate-pulse">Disconnected</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-game-golden">
                    <FaUsers className="text-xs" />
                    <span className="font-semibold text-sm">
                      {roomConnection.playersData.length}/{room.maxPlayers || '∞'}
                    </span>
                  </div>
                  <FaChevronDown className={`text-gray-400 text-xs transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                </Button>
              ) : (
                <span className="text-gray-500 text-sm italic px-3 py-2">Loading...</span>
              )
            )}
            isOpen={showPlayersDropdown}
            onOpenChange={setShowPlayersDropdown}
            align="right"
            minWidth="280px"
            maxWidth="320px"
            closeOnSelect={false}
            className="p-2"
          >
            <div className="max-h-64 overflow-y-auto">
              <div className="text-gray-300 font-semibold text-sm mb-2 px-2">
                Players in {room?.name}
              </div>
              {roomConnection.isLoading ? (
                <div className="flex items-center justify-center px-3 py-4">
                  <FaSpinner className="animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-400 text-sm">Loading players...</span>
                </div>
              ) : roomConnection.playersData.length > 0 ? (
                roomConnection.playersData.map((player) => (
                  <button
                    key={player.userId}
                    className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-game-medium/60 transition-all duration-150 rounded-md group text-left"
                    onClick={() => handlePlayerClick(player)}
                  >
                    <PlayerAvatar username={player.username} avatar={player.avatar} size="sm" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-200 font-medium text-sm group-hover:text-white block truncate">
                        {player.username}
                        {(player as any).isLocal && (
                          <span className="text-game-golden text-xs ml-1">(You)</span>
                        )}
                      </span>
                      {player.role && (
                        <span className="text-gray-400 text-xs capitalize">
                          {player.role}
                        </span>
                      )}
                    </div>
                    {player.status && (
                      <FaCircle
                        className={`text-xs flex-shrink-0 ${getStatusTextColor(player.status)}`}
                      />
                    )}
                  </button>
                ))
              ) : (
                <div className="text-gray-400 text-sm px-3 py-2 italic">
                  No other players in room
                </div>
              )}
            </div>
          </Dropdown>

          {/* Exit Button */}
          <Button
            variant="danger"
            onClick={() => void handleExitRoom()}
            type="button"
            title="Exit Island"
            className="px-3 h-10 text-sm"
          >
            <FaDoorOpen className="text-sm" />
            <span className="hidden sm:inline">Exit</span>
          </Button>
        </div>

        {/* Error */}
        {(statusError || roomConnection.error) && (
          <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-game-orange text-white px-4 py-2 text-sm font-medium rounded-lg shadow-lg animate-in slide-in-from-top-2 duration-200 z-[2002]">
            {statusError || roomConnection.error}
          </div>
        )}
      </div>
    </div>

    <Modal
      isOpen={isConfirmingConsole}
      onClose={() => setIsConfirmingConsole(false)}
      title="Open the platform console?"
      size="md"
    >
      <div className="space-y-6">
        <p className="text-gray-300 leading-relaxed">
          You will leave {room?.name ?? 'this island'} and can rejoin it from the island list.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setIsConfirmingConsole(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmPlatformAdmin}>
            Open console
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default UserInfoBar;
